import React, { useMemo } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import type { CharterPricingInventoryRow } from '~/types/charterPricingInventory';

import { charterAirportOptions, charterCabinOptions } from './charterAirports';

export interface PricingInventoryFormValues {
  depAirport: string;
  arrAirport: string;
  depDate: string;
  cabin: string;
  availableDaysBefore: string;
  waitlistSeat: string;
  adultPrice: string;
  childPrice: string;
  infantPrice: string;
}

interface Props {
  open: boolean;
  loading?: boolean;
  values: PricingInventoryFormValues;
  title: string;
  submitLabel: string;
  existingRows?: Array<CharterPricingInventoryRow>;
  isEditMode?: boolean;
  minimumWaitlistSeat?: number;
  onClose: () => void;
  onSubmit: () => void;
  onChange: (field: keyof PricingInventoryFormValues, value: string) => void;
}

const integerFieldPattern = /^\d*$/;

export default function CharterPricingInventoryDialog({
  open,
  loading,
  values,
  title,
  submitLabel,
  existingRows = [],
  isEditMode = false,
  minimumWaitlistSeat = 0,
  onClose,
  onSubmit,
  onChange,
}: Props): React.JSX.Element {
  const hasDuplicateRow = useMemo(() => {
    if (isEditMode || !values.depAirport || !values.arrAirport || !values.depDate || !values.cabin) {
      return false;
    }

    return existingRows.some(
      (row) =>
        row.depAirport === values.depAirport &&
        row.arrAirport === values.arrAirport &&
        row.date === values.depDate &&
        row.cabin === values.cabin,
    );
  }, [isEditMode, existingRows, values.depAirport, values.arrAirport, values.depDate, values.cabin]);
  const isWaitlistSeatInvalid =
    isEditMode && values.waitlistSeat.trim() !== '' && Number(values.waitlistSeat) < minimumWaitlistSeat;

  const handleIntegerChange =
    (field: keyof PricingInventoryFormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (integerFieldPattern.test(value)) {
        onChange(field, value);
      }
    };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { p: 1.25 } } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {hasDuplicateRow && (
            <Alert severity="warning">
              A row with the same departure airport, arrival airport, departure date, and cabin class already exists.
              Submitting will override the existing row.
            </Alert>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Departure airport"
              select
              value={values.depAirport}
              onChange={(event) => onChange('depAirport', event.target.value)}
              fullWidth
              required
            >
              {charterAirportOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Arrival airport"
              select
              value={values.arrAirport}
              onChange={(event) => onChange('arrAirport', event.target.value)}
              fullWidth
              required
            >
              {charterAirportOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Departure date"
            type="date"
            value={values.depDate}
            onChange={(event) => onChange('depDate', event.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Cabin"
            select
            value={values.cabin}
            onChange={(event) => onChange('cabin', event.target.value)}
            fullWidth
            required
          >
            {charterCabinOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Available N Days before Departure Date"
            type="number"
            value={values.availableDaysBefore}
            onChange={handleIntegerChange('availableDaysBefore')}
            fullWidth
            required
            inputProps={{ step: 1, inputMode: 'numeric' }}
          />
          <TextField
            label="Waitlist Seat"
            type="number"
            value={values.waitlistSeat}
            onChange={handleIntegerChange('waitlistSeat')}
            fullWidth
            required
            error={isWaitlistSeatInvalid}
            helperText={
              isWaitlistSeatInvalid
                ? `Waitlist Seat cannot be less than Total Booked (${minimumWaitlistSeat}).`
                : undefined
            }
            inputProps={{ step: 1, inputMode: 'numeric', min: isEditMode ? minimumWaitlistSeat : 0 }}
          />
          <TextField
            label="Adult Price"
            type="number"
            value={values.adultPrice}
            onChange={(event) => onChange('adultPrice', event.target.value)}
            fullWidth
            required
            error={values.adultPrice !== '' && Number(values.adultPrice) < 0}
            helperText={values.adultPrice !== '' && Number(values.adultPrice) < 0 ? 'Price cannot be less than 0' : ''}
          />
          <TextField
            label="Child Price"
            type="number"
            value={values.childPrice}
            onChange={(event) => onChange('childPrice', event.target.value)}
            fullWidth
            required
            error={values.childPrice !== '' && Number(values.childPrice) < 0}
            helperText={values.childPrice !== '' && Number(values.childPrice) < 0 ? 'Price cannot be less than 0' : ''}
          />
          <TextField
            label="Infant Price"
            type="number"
            value={values.infantPrice}
            onChange={(event) => onChange('infantPrice', event.target.value)}
            fullWidth
            required
            error={values.infantPrice !== '' && Number(values.infantPrice) < 0}
            helperText={
              values.infantPrice !== '' && Number(values.infantPrice) < 0 ? 'Price cannot be less than 0' : ''
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={loading}
          disabled={
            Object.values(values).some((value) => value.trim() === '') ||
            isWaitlistSeatInvalid ||
            Number(values.adultPrice) < 0 ||
            Number(values.childPrice) < 0 ||
            Number(values.infantPrice) < 0
          }
        >
          {submitLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
